import { useState, useEffect, useCallback } from "react";
import RejectionModal from "./RejectionModal";
import AnimatedModal from "@/components/base/AnimatedModal";
import { supabase } from "@/lib/supabase";

interface JobItem {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  posted_at: string;
  status: string;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  rejection_reason?: string;
  is_expired?: boolean;
}

export default function JobModeration() {
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [search, setSearch] = useState("");
  const [rejectTarget, setRejectTarget] = useState<JobItem | null>(null);
  const [detailTarget, setDetailTarget] = useState<JobItem | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      setError(null);
      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_jobs")
        .select("*")
        .order("posted_at", { ascending: false });

      if (supabaseError) throw supabaseError;
      setJobs((data || []) as JobItem[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const filteredJobs = jobs.filter((j) => {
    if (filter !== "all" && j.status !== filter) return false;
    if (search && !j.title.toLowerCase().includes(search.toLowerCase()) && !j.company.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleApprove = async (id: string) => {
    const { error: updateError } = await supabase
      .from("vietnamcv_jobs")
      .update({ status: "approved", rejection_reason: null, approved_at: new Date().toISOString() })
      .eq("id", id);

    if (updateError) {
      showToast("error", "Lỗi khi duyệt tin. Thử lại nhé.");
      return;
    }

    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status: "approved", rejection_reason: undefined } : j)));
    showToast("success", "Tin tuyển dụng đã được duyệt thành công!");
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;

    const { error: updateError } = await supabase
      .from("vietnamcv_jobs")
      .update({ status: "rejected", rejection_reason: reason })
      .eq("id", rejectTarget.id);

    if (updateError) {
      showToast("error", "Lỗi khi từ chối tin. Thử lại nhé.");
      return;
    }

    setJobs((prev) => prev.map((j) => (j.id === rejectTarget.id ? { ...j, status: "rejected", rejection_reason: reason } : j)));
    setRejectTarget(null);
    showToast("success", "Đã từ chối tin tuyển dụng.");
  };

  const handleDelete = async (id: string) => {
    const { error: deleteError } = await supabase
      .from("vietnamcv_jobs")
      .delete()
      .eq("id", id);

    if (deleteError) {
      showToast("error", "Lỗi khi xóa tin. Thử lại nhé.");
      return;
    }

    setJobs((prev) => prev.filter((j) => j.id !== id));
    showToast("success", "Đã xóa tin tuyển dụng.");
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold whitespace-nowrap"><i className="ri-time-line text-xs"></i>Chờ duyệt</span>;
      case "approved":
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap"><i className="ri-check-line text-xs"></i>Đã duyệt</span>;
      case "rejected":
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold whitespace-nowrap"><i className="ri-close-line text-xs"></i>Đã từ chối</span>;
      default:
        return null;
    }
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case "full-time": return "Toàn thời gian";
      case "part-time": return "Bán thời gian";
      case "contract": return "Hợp đồng";
      default: return type;
    }
  };

  return (
    <div>
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-fade-in-up ${
          toast.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-base`}></i>
          {toast.message}
        </div>
      )}

      <RejectionModal
        isOpen={!!rejectTarget}
        jobTitle={rejectTarget?.title || ""}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
      />

      {/* Job Detail Modal */}
      <AnimatedModal isOpen={!!detailTarget} onClose={() => setDetailTarget(null)} variant="fade-up">
        <div className="bg-background-50 rounded-2xl w-[92vw] max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
          <div className="sticky top-0 bg-background-50 flex items-center justify-between px-6 py-4 border-b border-background-200 z-10">
            <div>
              <h2 className="font-heading font-bold text-lg text-foreground-950">{detailTarget?.title}</h2>
              <p className="text-sm text-foreground-500">{detailTarget?.company}</p>
            </div>
            <button
              onClick={() => setDetailTarget(null)}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-foreground-500"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {detailTarget && (
            <div className="p-6 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-foreground-600 bg-secondary-100/70 px-2.5 py-1 rounded-full">
                  <i className="ri-map-pin-line text-xs"></i>
                  {detailTarget.location}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-accent-700 bg-accent-100/70 px-2.5 py-1 rounded-full font-medium">
                  <i className="ri-money-dollar-circle-line text-xs"></i>
                  {detailTarget.salary}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-foreground-500 bg-background-200/70 px-2.5 py-1 rounded-full">
                  <i className="ri-briefcase-4-line text-xs"></i>
                  {typeLabel(detailTarget.type)}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full">
                  {detailTarget.category}
                </span>
                {detailTarget.is_expired && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
                    <i className="ri-timer-line text-xs"></i>
                    Đã hết hạn
                  </span>
                )}
                {statusBadge(detailTarget.status)}
              </div>

              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                  <i className="ri-file-text-line text-primary-500"></i>
                  Mô tả công việc
                </h4>
                <p className="text-sm text-foreground-700 leading-relaxed">{detailTarget.description}</p>
              </div>

              {detailTarget.detail && detailTarget.detail !== detailTarget.description && (
                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-article-line text-primary-500"></i>
                    Mô tả chi tiết
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">{detailTarget.detail}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-accent-600"></i>
                    Yêu cầu
                  </h4>
                  <ul className="space-y-1.5">
                    {(detailTarget.requirements || []).map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-gift-line text-primary-500"></i>
                    Quyền lợi
                  </h4>
                  <ul className="space-y-1.5">
                    {(detailTarget.benefits || []).map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-star-line text-primary-400 mt-0.5 flex-shrink-0"></i>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t border-background-200">
                <p className="text-xs text-foreground-500">
                  Ngày đăng: <strong>{detailTarget.posted_at}</strong>
                  {detailTarget.rejection_reason && (
                    <span className="ml-3 text-red-600">
                      Lý do từ chối: <strong>{detailTarget.rejection_reason}</strong>
                    </span>
                  )}
                </p>
              </div>

              <div className="flex justify-end gap-3">
                {detailTarget.status === "pending" && (
                  <>
                    <button
                      onClick={() => { handleApprove(detailTarget.id); setDetailTarget(null); }}
                      className="px-4 py-2.5 bg-green-500 text-white rounded-full text-sm font-semibold hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-check-line mr-1.5"></i>Duyệt
                    </button>
                    <button
                      onClick={() => { setRejectTarget(detailTarget); setDetailTarget(null); }}
                      className="px-4 py-2.5 bg-red-500 text-white rounded-full text-sm font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-line mr-1.5"></i>Từ chối
                    </button>
                  </>
                )}
                <button
                  onClick={() => setDetailTarget(null)}
                  className="px-4 py-2.5 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Đóng
                </button>
              </div>
            </div>
          )}
        </div>
      </AnimatedModal>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-full sm:w-auto overflow-x-auto">
          {(["all", "pending", "approved", "rejected"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 text-xs font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filter === f ? "bg-background-50 text-foreground-950 shadow-sm" : "text-foreground-500 hover:text-foreground-700"
              }`}
            >
              {f === "all" ? "Tất cả" : f === "pending" ? "Chờ duyệt" : f === "approved" ? "Đã duyệt" : "Đã từ chối"}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm tin tuyển dụng..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="p-10 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-background-200/80 rounded"></div>
                  <div className="h-3 w-1/2 bg-background-200/50 rounded"></div>
                </div>
                <div className="h-7 w-24 bg-background-200/60 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-xl text-red-500"></i>
            </div>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              onClick={() => { setLoading(true); fetchJobs(); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-sm"></i>
              <span>Thử lại</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Tin tuyển dụng</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Trạng thái</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="max-w-[320px]">
                        <button
                          onClick={() => setDetailTarget(job)}
                          className="font-semibold text-foreground-900 text-left hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1.5"
                        >
                          <span className="line-clamp-1">{job.title}</span>
                          {job.is_expired && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-[10px] font-semibold whitespace-nowrap flex-shrink-0">
                              <i className="ri-timer-line text-[9px]"></i>Đã hết hạn
                            </span>
                          )}
                          <i className="ri-external-link-line text-xs text-foreground-400 flex-shrink-0"></i>
                        </button>
                        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{job.description}</p>
                        {job.status === "rejected" && job.rejection_reason && (
                          <p className="text-xs text-red-600 mt-1 flex items-start gap-1">
                            <i className="ri-information-line text-xs flex-shrink-0 mt-0.5"></i>
                            <span className="line-clamp-2">Lý do: {job.rejection_reason}</span>
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">{statusBadge(job.status)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {job.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleApprove(job.id)}
                              className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              title="Duyệt"
                            >
                              <i className="ri-check-line text-base"></i>
                            </button>
                            <button
                              onClick={() => setRejectTarget(job)}
                              className="w-8 h-8 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                              title="Từ chối"
                            >
                              <i className="ri-close-line text-base"></i>
                            </button>
                          </>
                        )}
                        {(job.status === "approved" || job.status === "rejected") && (
                          <button
                            onClick={() => handleDelete(job.id)}
                            className="w-8 h-8 flex items-center justify-center bg-foreground-100 text-foreground-500 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors cursor-pointer"
                            title="Xóa"
                          >
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        )}
                        {job.status === "rejected" && (
                          <button
                            onClick={() => handleApprove(job.id)}
                            className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                            title="Duyệt lại"
                          >
                            <i className="ri-refresh-line text-base"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredJobs.length === 0 && !loading && (
                  <tr>
                    <td colSpan={3} className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 flex items-center justify-center bg-background-100 rounded-full">
                          <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                        </div>
                        <p className="text-sm text-foreground-500">Không có tin tuyển dụng nào</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 px-2">
        <p className="text-xs text-foreground-500">
          Hiển thị <strong>{filteredJobs.length}</strong> tin tuyển dụng
        </p>
      </div>
    </div>
  );
}